// CRM reports (Blue Market only): the pipeline now, results in a period, and activity per person.
// Plain bars, no chart library: a handful of numbers that answer "how are we doing?" at a glance.
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { get } from '../api';
import { useAuth } from '../auth';
import { useLiveTopics } from '../live';
import { Page, useCrumbs } from './Shell';
import { rand } from './Pipeline';
import { Portfolio } from './Progress';

type Period = '90d' | '12m' | 'ytd';
interface Bucket { month: string; count: number; value_cents: number; weighted_cents: number }
interface MonthResult { month: string; won: number; won_cents: number; lost: number; lost_cents: number }
interface PersonRow { id: string | null; name: string; note: number; call: number; meeting: number; email: number; tasks_done: number; tasks_overdue: number; won: number }
interface ReportData {
  period: { key: Period; from: string; to: string };
  pipeline: { count: number; value_cents: number; weighted_cents: number; by_stage: { stage: string; count: number; value_cents: number; weighted_cents: number }[]; closing: Bucket[] };
  results: { won: { count: number; value_cents: number }; lost: { count: number; value_cents: number }; win_rate: number | null; win_rate_value: number | null;
             days_to_win: number | null; by_month: MonthResult[]; lost_reasons: { reason: string; count: number; value_cents: number }[] };
  activity: { total: number; people: PersonRow[]; timeline: { start: string; count: number }[]; bucket: 'week' | 'month' };
  people: { id: string; name: string }[];
}

const PERIODS: { key: Period; label: string }[] = [{ key: '90d', label: 'Last 90 days' }, { key: '12m', label: 'Last 12 months' }, { key: 'ytd', label: 'This year' }];
const STAGE: Record<string, string> = { qualifying: 'Qualifying', proposal: 'Proposal', negotiation: 'Negotiation' };
const monthName = (m: string, long = false) => {
  if (m === 'overdue') return 'Overdue';
  if (m === 'later') return 'Later';
  if (m === 'none') return 'No date';
  return new Date(m + '-01T00:00').toLocaleDateString('en-ZA', long ? { month: 'long', year: 'numeric' } : { month: 'short' });
};
const pct = (n: number, max: number) => `${max ? Math.max(n ? 2 : 0, (100 * n) / max) : 0}%`;

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return <div className="tile static"><span className="tile-label">{label}</span><span className="tile-value">{value}</span>{sub && <span className="tile-sub">{sub}</span>}</div>;
}

/** A horizontal bar: the full value, with the weighted (or won) part darker. */
function HBar({ label, value, part, max, right }: { label: string; value: number; part?: number; max: number; right: string }) {
  return (
    <div className="hbar">
      <span className="hbar-label small">{label}</span>
      <span className="hbar-track"><span className="hbar-fill" style={{ width: pct(value, max) }} />{part != null && <span className="hbar-part" style={{ width: pct(part, max) }} />}</span>
      <span className="hbar-value small">{right}</span>
    </div>
  );
}

export function ReportsPage() {
  const { can } = useAuth();
  const setCrumbs = useCrumbs();
  useEffect(() => setCrumbs([{ label: 'Reports' }]), [setCrumbs]);
  useLiveTopics('workspace');
  const [search, setSearch] = useSearchParams();
  const views = [can('delivery.view') && 'delivery', can('crm.view') && 'sales'].filter(Boolean) as string[];
  const view = views.includes(search.get('view') ?? '') ? search.get('view')! : views[0];
  const tabs = views.length > 1 && (
    <nav className="tabs">{views.map((v) => <a key={v} href={`?view=${v}`} className={view === v ? 'active' : ''} onClick={(e) => { e.preventDefault(); setSearch({ view: v }); }}>{v === 'sales' ? 'Sales' : 'Delivery'}</a>)}</nav>);
  if (view === 'delivery') return <Page title="Reports">{tabs}<Portfolio /></Page>;
  return <SalesReports tabs={tabs} />;
}

function SalesReports({ tabs }: { tabs: React.ReactNode }) {
  const { me } = useAuth();
  const [period, setPeriod] = useState<Period>(() => { try { return (localStorage.getItem('bmp:reports:period') as Period) || '90d'; } catch { return '90d'; } });
  const [owner, setOwner] = useState('');
  useEffect(() => { try { localStorage.setItem('bmp:reports:period', period); } catch { /* private mode */ } }, [period]);
  const { data } = useQuery({ queryKey: ['crm-reports', period, owner], queryFn: () => get<ReportData>(`/api/crm/reports?period=${period}${owner ? `&owner=${owner}` : ''}`) });

  const actions = <>
    <select className="input compact" value={period} onChange={(e) => setPeriod(e.target.value as Period)} aria-label="Period">
      {PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
    </select>
    <select className="input compact" value={owner} onChange={(e) => setOwner(e.target.value)} aria-label="Whose opportunities">
      <option value="">Everyone's</option>{data?.people.map((p) => <option key={p.id} value={p.id}>{p.id === me?.id ? 'Mine' : p.name}</option>)}
    </select>
  </>;
  if (!data) return <Page title="Reports" actions={actions}>{tabs}<span className="spin muted" /></Page>;
  const { pipeline: p, results: r, activity: a } = data;
  const stageMax = Math.max(...p.by_stage.map((s) => s.value_cents), 1);
  const closing = p.closing.filter((c) => c.count || !['overdue', 'later', 'none'].includes(c.month));
  const closeMax = Math.max(...closing.map((c) => c.value_cents), 1);
  const monthMax = Math.max(...r.by_month.map((m) => Math.max(m.won_cents, m.lost_cents)), 1);
  const timeMax = Math.max(...a.timeline.map((t) => t.count), 1);
  const periodLabel = PERIODS.find((x) => x.key === period)!.label.toLowerCase();
  const nothing = !p.count && !r.won.count && !r.lost.count && !a.total;

  return (
    <Page title="Reports" actions={actions}>
      {tabs}
      <div className="tiles">
        <Stat label="Open pipeline" value={rand(p.value_cents, true)} sub={`${rand(p.weighted_cents, true)} weighted · ${p.count} open`} />
        <Stat label={`Won, ${periodLabel}`} value={rand(r.won.value_cents, true)} sub={`${r.won.count} won · ${r.lost.count} lost`} />
        <Stat label="Win rate" value={r.win_rate == null ? '–' : `${r.win_rate}%`} sub={r.win_rate_value == null ? 'Nothing closed yet' : `${r.win_rate_value}% by value`} />
        <Stat label="Time to win" value={r.days_to_win == null ? '–' : `${r.days_to_win} days`} sub="from added to won, on average" />
      </div>
      {nothing && <p className="muted small">Nothing to report yet. Opportunities, activity and tasks show up here as you add them.</p>}

      <div className="grid-2 reports">
        <section className="card pad stack">
          <div><h2>Open pipeline by stage</h2><p className="muted tiny" style={{ margin: '2px 0 0' }}>Darker: weighted by probability</p></div>
          {p.by_stage.map((s) => <HBar key={s.stage} label={`${STAGE[s.stage]} (${s.count})`} value={s.value_cents} part={s.weighted_cents} max={stageMax} right={rand(s.value_cents, true)} />)}
        </section>
        <section className="card pad stack">
          <div><h2>Expected to close</h2><p className="muted tiny" style={{ margin: '2px 0 0' }}>Open opportunities by expected close date</p></div>
          {closing.map((c) => <HBar key={c.month} label={`${monthName(c.month)}${c.count ? ` (${c.count})` : ''}`} value={c.value_cents} part={c.weighted_cents} max={closeMax}
                                    right={c.count ? rand(c.value_cents, true) : '–'} />)}
        </section>
        <section className="card pad stack">
          <div><h2>Won and lost</h2><p className="muted tiny" style={{ margin: '2px 0 0' }}>By month closed, {periodLabel}</p></div>
          <div className="vbars" role="img" aria-label="Won and lost value per month">
            {r.by_month.map((m) => (
              <div key={m.month} className="vbar-col" title={`${monthName(m.month, true)}: won ${rand(m.won_cents)} (${m.won}), lost ${rand(m.lost_cents)} (${m.lost})`}>
                <div className="vbar-pair"><span className="vbar won" style={{ height: pct(m.won_cents, monthMax) }} /><span className="vbar lost" style={{ height: pct(m.lost_cents, monthMax) }} /></div>
                <span className="tiny muted">{monthName(m.month)}</span>
              </div>
            ))}
          </div>
          <div className="row tiny muted" style={{ gap: 14 }}><span><span className="key won" />Won {rand(r.won.value_cents, true)}</span><span><span className="key lost" />Lost {rand(r.lost.value_cents, true)}</span></div>
        </section>
        <section className="card pad stack">
          <h2>Why we lose</h2>
          {r.lost_reasons.map((x) => (
            <div key={x.reason} className="row small" style={{ gap: 10 }}>
              <span className="grow">{x.reason}</span><span className="muted tiny">{rand(x.value_cents, true)}</span><b style={{ minWidth: 20, textAlign: 'right' }}>{x.count}</b>
            </div>
          ))}
          {!r.lost_reasons.length && <p className="muted small" style={{ margin: 0 }}>Nothing lost {periodLabel}.</p>}
        </section>
      </div>

      <section className="card" style={{ marginTop: 16 }}>
        <div className="card-head"><h2>Activity</h2><span className="muted tiny">{a.total} logged {periodLabel}</span></div>
        <div className="spark" role="img" aria-label={`Activity per ${a.bucket}`}>
          {a.timeline.map((t) => <span key={t.start} className="spark-bar" style={{ height: pct(t.count, timeMax) }}
                                       title={`${a.bucket === 'week' ? `Week of ${new Date(t.start + 'T00:00').toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}` : monthName(t.start, true)}: ${t.count}`} />)}
        </div>
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Person</th><th className="num">Calls</th><th className="num">Meetings</th><th className="num">Emails</th><th className="num hide-sm">Notes</th><th className="num">Tasks done</th><th className="num">Overdue</th><th className="num hide-sm">Won</th></tr></thead>
            <tbody>
              {a.people.map((x) => (
                <tr key={x.id ?? x.name}>
                  <td><b>{x.name}</b></td><td className="num">{x.call || '–'}</td><td className="num">{x.meeting || '–'}</td><td className="num">{x.email || '–'}</td><td className="num hide-sm">{x.note || '–'}</td>
                  <td className="num">{x.tasks_done || '–'}</td><td className={`num ${x.tasks_overdue ? 'bad-text' : ''}`}>{x.tasks_overdue || '–'}</td><td className="num hide-sm">{x.won || '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!a.people.length && <div className="empty small">No activity or tasks {periodLabel}.</div>}
        </div>
      </section>
    </Page>
  );
}
